import {
  AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { CompanyNotesService } from 'src/app/core/data-services/company';
import { ImageViewerService } from 'src/app/shared/services/image-viewer.service';

@Component({
    selector: 'rms-text-content-body',
    templateUrl: './text-content-body.component.html',
    styleUrls: ['./text-content-body.component.scss'],
    standalone: false
})
export class TextContentBodyComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked {

  @Input() data: string;
  @Input() isOpen: boolean;
  @ViewChild('noteContainer', { static: true }) noteContainer: ElementRef;

  private _lineHeight = 18;
  private _maxLines = 30;
  private _paddings = 28;
  private _viewInit = false;
  /** The Expand/Condense Notes setting: whether a long note may be clamped at all. */
  private _showMoreEnabled = true;
  /** Set when the content changes after first render, so the clamp is re-measured against it. */
  private _measurePending = false;

  public linesView = '';
  public displayShowMore = true;
  public showMore = false;
  public containerStyle = {};

  constructor(
    private cdRef: ChangeDetectorRef,
    private companyNotesService: CompanyNotesService,
    private imageViewerService: ImageViewerService
  ) {}

  /**
   * Content can change after first render when a list re-binds a re-fetched note to this same
   * instance (e.g. after the note is edited). The clamp has to be measured against the new
   * content, which only exists in the DOM once the view has been checked.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && !changes.data.firstChange) {
      this._measurePending = true;
    }
  }

  ngAfterViewInit(): void {
    this._setShowMore();
    this._viewInit = true;
    this.cdRef.detectChanges();
  }

  ngAfterViewChecked(): void {
    if (this._measurePending && this._viewInit) {
      this._measurePending = false;
      this._setShowMore();
      this.cdRef.detectChanges();
    }
  }

  ngOnInit() {
    this.companyNotesService.getShowCompleteNote().subscribe(show => {
      this._showMoreEnabled = show;
      this.displayShowMore = show;
      if (this._viewInit) {
        this._setShowMore();
      }
    });
  }

  /**
   * Show more action click
   */
  public onShowMore(): void {
    this.showMore = !this.showMore;
    this._applyShowMore();
  }

  /**
   * Handles clicks on the note container.
   * If the clicked element is an image, opens it in the fullscreen viewer.
   * If the clicked element is (or is inside) an anchor, opens the link in a new tab
   * so the user does not lose their place in RMS (RA-2042).
   */
  public onContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const imgSrc = (target as HTMLImageElement).src;
      if (imgSrc) {
        this.imageViewerService.open(imgSrc);
      }
      return;
    }

    const anchor = target.closest && target.closest('a');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (this._shouldOpenInNewTab(href)) {
        event.preventDefault();
        event.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }
  }

  /**
   * Returns true when the href should be hijacked and opened in a new tab.
   * Skips empty/hash links, javascript: URLs, and mailto:/tel: which should use default behavior.
   */
  private _shouldOpenInNewTab(href: string | null): boolean {
    if (!href) {
      return false;
    }
    const trimmed = href.trim();
    if (!trimmed || trimmed === '#') {
      return false;
    }
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('mailto:') || lower.startsWith('tel:')) {
      return false;
    }
    return true;
  }

  private _applyShowMore(): void {
    this.containerStyle = !this.showMore ? {'max-height': this.linesView} : {};
  }

  private _setShowMore(): void {
    const maxHeight = (this._lineHeight * this._maxLines) + this._paddings;
    this.displayShowMore = this._showMoreEnabled && this.noteContainer.nativeElement.offsetHeight >= maxHeight;
    this.linesView = `${maxHeight}px`;
    this.containerStyle = this.displayShowMore && !this.showMore ? {'max-height': this.linesView} : {};
  }

}
